/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

import { AnalyticsActionPayload } from "@/store/analytics-store"

type FlowKey = "OCR" | "C1"

type FlowStep = {
  path: string
  nextStepPath?: string
  prevStepPath?: string
}

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/

const flowC1: FlowStep[] = [
  {
    path: "/personal-data",
    nextStepPath: "/personal-data-address",
    prevStepPath: "/",
  },
  {
    path: "/personal-data-address",
    nextStepPath: "/personal-data-document",
    prevStepPath: "/personal-data",
  },
  {
    path: "/personal-data-document",
    nextStepPath: "/contacts",
    prevStepPath: "/personal-data-address",
  },
  {
    path: "/contacts",
    nextStepPath: "/credentials",
    prevStepPath: "/personal-data-document",
  },
  {
    path: "/credentials",
    nextStepPath: "/recharge-limits",
    prevStepPath: "/contacts",
  },
  {
    path: "/recharge-limits",
    prevStepPath: "/credentials",
    nextStepPath: "/signup-bonus",
  },
  {
    path: "/signup-bonus",
    nextStepPath: "/terms-and-conditions",
    prevStepPath: "/recharge-limits",
  },
  {
    path: "/terms-and-conditions",
    prevStepPath: "/signup-bonus",
  },
]

const flowOCR: FlowStep[] = [
  {
    path: "/recap-ocr",
    nextStepPath: "/personal-data-address",
    prevStepPath: "/",
  },
  {
    path: "/personal-data-address",
    nextStepPath: "/contacts",
    prevStepPath: "/recap-ocr",
  },
  {
    path: "/contacts",
    nextStepPath: "/credentials",
    prevStepPath: "/personal-data-address",
  },
  {
    path: "/credentials",
    nextStepPath: "/recharge-limits",
    prevStepPath: "/contacts",
  },
  {
    path: "/recharge-limits",
    nextStepPath: "/signup-bonus",
    prevStepPath: "/credentials",
  },
  {
    path: "/signup-bonus",
    prevStepPath: "/recharge-limits",
    nextStepPath: "/terms-and-conditions",
  },
  {
    path: "/terms-and-conditions",
    prevStepPath: "/signup-bonus",
  },
]

const analyticsPageByFlow: Record<
  FlowKey | "no-flow",
  Record<string, AnalyticsActionPayload>
> = {
  C1: {
    "nome-cognome": {
      page_name: "registrazione:standard:step_1:come_ti_chiami",
      conversion_pagename: "registrazione:standard:step_1:come_ti_chiami",
      flusso_registrazione: "standard",
    },
    "codice-fiscale": {
      page_name: "registrazione:standard:step_2:codice_fiscale",
      conversion_pagename: "registrazione:standard:step_2:codice_fiscale",
      flusso_registrazione: "standard",
      calculate_cf_dialog_conversion_pagename:
        "registrazione:standard:step_2:calcolo_codice_fiscale",
    },
    "indirizzo-residenza": {
      page_name: "registrazione:standard:step_3:indirizzo",
      conversion_pagename: "registrazione:standard:step_3:indirizzo",
      flusso_registrazione: "standard",
    },
    contatti: {
      page_name: "registrazione:standard:step_4:email_cellulare",
      conversion_pagename: "registrazione:standard:step_4:email_cellulare",
      flusso_registrazione: "standard",
      verify_otp_conversion_pagename:
        "registrazione:standard:step_4:verifica_cel_otp",
    },
    "dati-di-accesso": {
      page_name: "registrazione:standard:step_5:username_password",
      conversion_pagename: "registrazione:standard:step_5:username_password",
      flusso_registrazione: "standard",
    },
    bonus: {
      page_name: "registrazione:standard:step_6:bonus_di_benvenuto",
      conversion_pagename: "registrazione:standard:step_6:bonus_di_benvenuto",
      flusso_registrazione: "standard",
      bonus_detail_dialog_conversion_pagename:
        "registrazione:standard:step_6:dettaglio_bonus",
      bonus_code_dialog_conversion_pagename:
        "registrazione:standard:step_6:bonus_di_benvenuto_codice",
    },
    "dati-documento": {
      page_name: "registrazione:standard:step_7:dati_documento",
      conversion_pagename: "registrazione:standard:step_7:dati_documento",
      flusso_registrazione: "standard",
    },
    "limiti-ricarica": {
      page_name: "registrazione:standard:step_8:limite_ricarica",
      conversion_pagename: "registrazione:standard:step_8:limite_ricarica",
      flusso_registrazione: "standard",
    },
    "termini-e-condizioni": {
      page_name: "registrazione:standard:step_9:condizioni_privacy",
      conversion_pagename: "registrazione:standard:step_9:condizioni_privacy",
      flusso_registrazione: "standard",
    },
    recap: {
      page_name: "registrazione:standard:step_10:riepilogo",
      conversion_pagename: "registrazione:standard:step_10:riepilogo",
      flusso_registrazione: "standard",
      residence_dialog_conversion_pagename:
        "registrazione:standard:step_10:dettaglio_residenza_domicilio",
    },
    thankyou: {
      page_name: "registrazione:standard:typ",
      conversion_pagename: "registrazione:standard:typ",
      flusso_registrazione: "standard",
    },
    "errore-generico": {
      page_name: "registrazione:standard:errore_generico",
      conversion_pagename: "registrazione:standard:errore_generico",
      flusso_registrazione: "standard",
    },
    "errore-auto-esclusione": {
      page_name: "registrazione:standard:errore_auto_esclusione",
      conversion_pagename: "registrazione:standard:errore_auto_esclusione",
      flusso_registrazione: "standard",
    },
    "errore-conto-chiuso": {
      page_name: "registrazione:standard:errore_conto_chiuso",
      conversion_pagename: "registrazione:standard:errore_conto_chiuso",
      flusso_registrazione: "standard",
    },
    "errore-centenario": {
      page_name: "registrazione:standard:errore_centenario",
      conversion_pagename: "registrazione:standard:errore_centenario",
      flusso_registrazione: "standard",
    },
    "errore-minore": {
      page_name: "registrazione:standard:errore_minore",
      conversion_pagename: "registrazione:standard:errore_minore",
      flusso_registrazione: "standard",
    },
    "errore-cf-presente": {
      page_name: "registrazione:standard:error_page:conto_gia_aperto",
      conversion_pagename: "registrazione:standard:error_page:conto_gia_aperto",
      flusso_registrazione: "standard",
    },
    "errore-cf-multiaccount": {
      page_name: "registrazione:standard:errore_generico",
      conversion_pagename: "registrazione:standard:errore_generico",
      flusso_registrazione: "standard",
    },
    "errore-conto-aperto-pdv": {
      page_name: "registrazione:standard:errore_conto_aperto-pdv",
      conversion_pagename: "registrazione:standard:errore_conto_aperto-pdv",
      flusso_registrazione: "standard",
    },
    "errore-dati-incongruenti": {
      page_name: "registrazione:standard:errore_dati_incongruenti",
      conversion_pagename: "registrazione:standard:errore_dati_incongruenti",
      flusso_registrazione: "standard",
    },
    "errore-no-dipendenti": {
      page_name: "registrazione:standard:errore_no_dipendenti",
      conversion_pagename: "registrazione:standard:errore_no_dipendenti",
      flusso_registrazione: "standard",
    },
    "errore-no-promo": {
      page_name: "registrazione:standard:errore_no_promo",
      conversion_pagename: "registrazione:standard:errore_no_promo",
      flusso_registrazione: "standard",
    },
    "errore-ocr-malfunzionamento": {
      page_name: "registrazione:standard:errore_ocr_malfunzionamento",
      conversion_pagename: "registrazione:standard:errore_ocr_malfunzionamento",
      flusso_registrazione: "standard",
    },
    "errore-ocr-non-supportato": {
      page_name: "registrazione:standard:errore_ocr_non_supportato",
      conversion_pagename: "registrazione:standard:errore_ocr_non_supportato",
      flusso_registrazione: "standard",
    },
    "errore-spid-credenziali-incompatibili": {
      page_name: "registrazione:standard:errore_spid_credenziali_incompatibili",
      conversion_pagename:
        "registrazione:standard:errore_spid_credenziali_incompatibili",
      flusso_registrazione: "standard",
    },
  },

  OCR: {
    "seleziona-documento": {
      page_name: "registrazione:ocr:step_1:seleziona_documento",
      conversion_pagename: "registrazione:ocr:step_1:seleziona_documento",
      flusso_registrazione: "ocr",
    },
    "fotografa-documento": {
      page_name: "registrazione:ocr:step_2:fotografa_documento",
      conversion_pagename: "registrazione:ocr:step_2:fotografa_documento",
      flusso_registrazione: "ocr",
    },
    "riepilogo-dati-ocr": {
      page_name: "registrazione:ocr:step_5:verifica_dati",
      conversion_pagename: "registrazione:ocr:step_5:verifica_dati",
      flusso_registrazione: "ocr",
    },
    contatti: {
      page_name: "registrazione:ocr:step_6:email_cellulare",
      conversion_pagename: "registrazione:ocr:step_6:email_cellulare",
      flusso_registrazione: "ocr",
    },
    "dati-di-accesso": {
      page_name: "registrazione:ocr:step_7:username_password",
      conversion_pagename: "registrazione:ocr:step_7:username_password",
      flusso_registrazione: "ocr",
    },
    bonus: {
      page_name: "registrazione:ocr:step_8:bonus_di_benvenuto",
      conversion_pagename: "registrazione:ocr:step_8:bonus_di_benvenuto",
      flusso_registrazione: "ocr",
      bonus_detail_dialog_conversion_pagename:
        "registrazione:ocr:step_8:dettaglio_bonus",
      bonus_code_dialog_conversion_pagename:
        "registrazione:ocr:step_8:bonus_di_benvenuto_codice",
    },
    "limiti-ricarica": {
      page_name: "registrazione:ocr:step_9:limite_ricarica",
      conversion_pagename: "registrazione:ocr:step_9:limite_ricarica",
      flusso_registrazione: "ocr",
    },
    "termini-e-condizioni-ocr": {
      page_name: "registrazione:ocr:step_10:condizioni_privacy",
      conversion_pagename: "registrazione:ocr:step_10:condizioni_privacy",
      flusso_registrazione: "ocr",
    },
    "errore-generico": {
      page_name: "registrazione:ocr:errore_generico",
      conversion_pagename: "registrazione:ocr:errore_generico",
      flusso_registrazione: "ocr",
    },
    "errore-auto-esclusione": {
      page_name: "registrazione:ocr:errore_auto_esclusione",
      conversion_pagename: "registrazione:ocr:errore_auto_esclusione",
      flusso_registrazione: "ocr",
    },
    "errore-conto-chiuso": {
      page_name: "registrazione:ocr:errore_conto_chiuso",
      conversion_pagename: "registrazione:ocr:errore_conto_chiuso",
      flusso_registrazione: "ocr",
    },
    "errore-centenario": {
      page_name: "registrazione:ocr:errore_centenario",
      conversion_pagename: "registrazione:ocr:errore_centenario",
      flusso_registrazione: "ocr",
    },
    "errore-minore": {
      page_name: "registrazione:ocr:errore_minore",
      conversion_pagename: "registrazione:ocr:errore_minore",
      flusso_registrazione: "ocr",
    },
    "errore-cf-presente": {
      page_name: "registrazione:ocr:error_page:conto_gia_aperto",
      conversion_pagename: "registrazione:ocr:error_page:conto_gia_aperto",
      flusso_registrazione: "ocr",
    },
    "errore-cf-multiaccount": {
      page_name: "registrazione:ocr:errore_generico",
      conversion_pagename: "registrazione:ocr:errore_generico",
      flusso_registrazione: "ocr",
    },
    "errore-conto-aperto-pdv": {
      page_name: "registrazione:ocr:errore_conto_aperto-pdv",
      conversion_pagename: "registrazione:ocr:errore_conto_aperto-pdv",
      flusso_registrazione: "ocr",
    },
    "errore-dati-incongruenti": {
      page_name: "registrazione:ocr:errore_dati_incongruenti",
      conversion_pagename: "registrazione:ocr:errore_dati_incongruenti",
      flusso_registrazione: "ocr",
    },
    "errore-no-dipendenti": {
      page_name: "registrazione:ocr:errore_no_dipendenti",
      conversion_pagename: "registrazione:ocr:errore_no_dipendenti",
      flusso_registrazione: "ocr",
    },
    "errore-no-promo": {
      page_name: "registrazione:ocr:errore_no_promo",
      conversion_pagename: "registrazione:ocr:errore_no_promo",
      flusso_registrazione: "ocr",
    },
    "errore-ocr-malfunzionamento": {
      page_name: "registrazione:ocr:errore_ocr_malfunzionamento",
      conversion_pagename: "registrazione:ocr:errore_ocr_malfunzionamento",
      flusso_registrazione: "ocr",
    },
    "errore-ocr-non-supportato": {
      page_name: "registrazione:ocr:errore_ocr_non_supportato",
      conversion_pagename: "registrazione:ocr:errore_ocr_non_supportato",
      flusso_registrazione: "ocr",
    },
    "errore-spid-credenziali-incompatibili": {
      page_name: "registrazione:ocr:errore_spid_credenziali_incompatibili",
      conversion_pagename:
        "registrazione:ocr:errore_spid_credenziali_incompatibili",
      flusso_registrazione: "ocr",
    },
    thankyou: {
      page_name: "registrazione:ocr:typ",
      conversion_pagename: "registrazione:ocr:typ",
      flusso_registrazione: "ocr",
    },
  },

  "no-flow": {
    "errore-generico": {
      page_name: "registrazione:errore_generico",
      conversion_pagename: "registrazione:errore_generico",
    },
  },
}

//VERRA' SPOSTATO IN POSTO PIU APPROPRIATI QUANDO RIFARO STA COSA
const analyticsFlowNameByActualFlowName = {
  C1: "standard",
  OCR: "ocr",
  "no-flow": "no-flow",
}

const flowsMapping = {
  C1: flowC1,
  OCR: flowOCR,
}

export { analyticsFlowNameByActualFlowName, analyticsPageByFlow, flowsMapping }
export type { FlowKey }
